// src/components/PrintScheduler.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Button,
  Spinner,
  VStack,
  Checkbox,
  useToast,
  Flex,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ScheduledSectorsPDF from "./ScheduledSectorsPDF";
import useBusinessGuard from "../hooks/useBusinessGuard";

function getSeasonStartDate(season, year) {
  if (!season || !year) return null;
  const y = Number(year);
  switch (season) {
    case "Q1":
      return new Date(y, 0, 1); // Jan 1
    case "Q2":
      return new Date(y, 3, 1); // Apr 1
    case "Q3":
      return new Date(y, 6, 1); // Jul 1
    case "Q4":
      return new Date(y, 9, 1); // Oct 1
    default:
      return null;
  }
}

export default function PrintScheduler({ campaignId, region, group }) {
  const toast = useToast();
  const { businessUUID, loading: authLoading } = useBusinessGuard();
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);

  const REQUIRED_SLOTS = 25;
  const MAX_SLOTS = 30;

  useEffect(() => {
    if (authLoading || !businessUUID || !campaignId) return;

    const fetchQueue = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from("dispatch_queue")
          .select("*")
          .eq("business_id", businessUUID)
          .eq("campaign_id", campaignId)
          .is("scheduled_print_at", null)
          .order("ready_at", { ascending: true });

        if (region) query = query.eq("region", region);
        if (group) query = query.eq("group_id", group);

        const { data, error } = await query;
        if (error) throw new Error(error.message);

        const baseQueue = data || [];

        if (baseQueue.length === 0) {
          setQueue([]);
          return;
        }

        const sectors = Array.from(
          new Set(
            baseQueue
              .map((q) => q.postcode_sector)
              .filter((s) => typeof s === "string" && s.length > 0)
          )
        );

        let flyersQuery = supabase
          .from("flyers")
          .select("id, postcode_sector, season, year, campaign_id, group_id")
          .eq("campaign_id", campaignId);

        if (group) flyersQuery = flyersQuery.eq("group_id", group);
        if (sectors.length > 0) flyersQuery = flyersQuery.in("postcode_sector", sectors);

        const { data: flyersData, error: flyersError } = await flyersQuery;
        if (flyersError) throw new Error(flyersError.message);

        const flyerMap = {};
        (flyersData || []).forEach((f) => {
          if (!flyerMap[f.postcode_sector]) {
            flyerMap[f.postcode_sector] = f;
          }
        });

        const enrichedQueue = baseQueue.map((entry) => ({
          ...entry,
          flyer: flyerMap[entry.postcode_sector] || null,
        }));

        setQueue(enrichedQueue);
      } catch (err) {
        toast({
          title: "Error loading queue",
          description: err.message,
          status: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchQueue();
  }, [authLoading, businessUUID, campaignId, region, group, toast]);

  const moveSlotsToNearbySector = async (entry) => {
    const { data: nearby } = await supabase.rpc("find_nearby_sectors", {
      sector: entry.postcode_sector,
    });

    const target = nearby?.[0]?.name;
    if (!target) return entry.postcode_sector;

    const { data: slots } = await supabase
      .from("flyer_slots")
      .select("id")
      .eq("campaign_id", campaignId)
      .eq("postcode", entry.postcode_sector)
      .eq("status", "paid");

    for (const slot of slots || []) {
      await supabase
        .from("flyer_slots")
        .update({ postcode: target })
        .eq("id", slot.id);
    }

    await supabase.from("notifications").insert({
      business_id: businessUUID,
      campaign_id: campaignId,
      postcode: entry.postcode_sector,
      type: "sector_moved",
      message: `Sector ${entry.postcode_sector} had fewer than ${REQUIRED_SLOTS} slots. All paid flyers moved to nearby sector ${target}.`,
      region,
      group_id: group,
    });

    return target;
  };

  const handleSchedule = async () => {
    setScheduling(true);
    try {
      const now = new Date().toISOString();
      const updated = queue.filter((q) => selected.includes(q.id));

      for (const entry of updated) {
        let targetSector = entry.postcode_sector;

        if (entry.flyer_count < REQUIRED_SLOTS) {
          targetSector = await moveSlotsToNearbySector(entry);
        }

        await supabase
          .from("dispatch_queue")
          .update({ scheduled_print_at: now })
          .eq("id", entry.id);

        await supabase.from("notifications").insert({
          business_id: businessUUID,
          campaign_id: campaignId,
          postcode: targetSector,
          type: "print_scheduled",
          message: `Print scheduled for ${targetSector}. Branding fills remaining slots.`,
          region,
          group_id: group,
        });

        // Find or fetch flyer for this sector to drive seasonal email
        let flyer = entry.flyer;
        if (!flyer || flyer.postcode_sector !== targetSector) {
          const { data: flyerData } = await supabase
            .from("flyers")
            .select("id, postcode_sector, season, year, campaign_id, group_id")
            .eq("campaign_id", campaignId)
            .eq("postcode_sector", targetSector)
            .maybeSingle();
          flyer = flyerData || null;
        }

        if (flyer?.id) {
          await supabase.functions.invoke("campaign-emails", {
            body: {
              type: "print_scheduled",
              flyer_id: flyer.id,
            },
          });
        }
      }

      toast({
        title: "Print Scheduler",
        description: `${selected.length} sectors processed.`,
        status: "success",
      });

      setSelected([]);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    } finally {
      setScheduling(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (authLoading || !businessUUID || !campaignId) return null;

  return (
    <Box p={4} bg="gray.50" borderRadius="md">
      <Text fontWeight="bold" mb={2}>
        🖨️ Print Scheduler (Seasonal)
      </Text>

      {loading ? (
        <Spinner />
      ) : queue.length === 0 ? (
        <Text>No sectors waiting for print.</Text>
      ) : (
        <>
          <VStack align="start" spacing={2} mb={4}>
            {queue.map((entry) => {
              const flyer = entry.flyer;
              let deliveryLabel = "Est. delivery: TBC";

              if (flyer?.season && flyer?.year) {
                const seasonStart = getSeasonStartDate(flyer.season, flyer.year);
                if (seasonStart) {
                  const deliveryDate = new Date(seasonStart);
                  deliveryDate.setDate(deliveryDate.getDate() + 14);
                  deliveryLabel = `Est. delivery: ${deliveryDate.toLocaleDateString()}`;
                }
              }

              return (
                <Checkbox
                  key={entry.id}
                  isChecked={selected.includes(entry.id)}
                  onChange={() => toggleSelect(entry.id)}
                >
                  {entry.postcode_sector} → {entry.flyer_count} / {MAX_SLOTS} slots —{" "}
                  {deliveryLabel}
                </Checkbox>
              );
            })}
          </VStack>

          <Flex gap={4}>
            <Button
              colorScheme="blue"
              onClick={handleSchedule}
              isLoading={scheduling}
            >
              Schedule Print
            </Button>

            <PDFDownloadLink
              document={
                <ScheduledSectorsPDF
                  sectors={queue.filter((q) => q.scheduled_print_at)}
                />
              }
              fileName="scheduled_sectors.pdf"
            >
              {({ loading }) => (
                <Button colorScheme="gray" isLoading={loading}>
                  {loading ? "Preparing PDF..." : "Export PDF"}
                </Button>
              )}
            </PDFDownloadLink>
          </Flex>
        </>
      )}
    </Box>
  );
}
